import React, { useEffect, useState, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "../../../services/api";
import { CALL_STATUS_OPTIONS, CALL_TYPE_OPTIONS } from "../../../constants/grants.constants";

interface CreateCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  callId?: string | null;
}

interface CallFormData {
  title: string;
  programId: string;
  operatorId: string;
  competitionId: string;
  projectId: string;
  callType: string;
  status: string;
  grantType: string;
  applicationStartDate: string;
  applicationEndDate: string;
  announcedAt: string;
  allocation: string;
  fundingMin: string;
  fundingMax: string;
  fundingPercentage: string;
  fundingCurrency: string;
  targetRegions: string;
  targetGroup: string;
  officialUrl: string;
  applicationUrl: string;
}


const CreateCallModal: React.FC<CreateCallModalProps> = ({
  isOpen,
  onClose,
  callId,
}) => {
  const isEditMode = Boolean(callId);
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<CallFormData>({
    title: "",
    programId: "",
    operatorId: "",
    competitionId: "",
    projectId: "",
    callType: "COMPETITIVE",
    status: "ANNOUNCED",
    grantType: "",
    applicationStartDate: "",
    applicationEndDate: "",
    announcedAt: "",
    allocation: "",
    fundingMin: "",
    fundingMax: "",
    fundingPercentage: "",
    fundingCurrency: "PLN",
    targetRegions: "",
    targetGroup: "",
    officialUrl: "",
    applicationUrl: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [globalError, setGlobalError] = useState<string | null>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);

  const { data: programsData, isLoading: isLoadingPrograms } = useQuery({
    queryKey: ["grants-programs"],
    queryFn: async () => {
      const { data } = await apiClient.get("/grants/programs");
      return data;
    },
    enabled: isOpen,
  });

  const { data: operatorsData, isLoading: isLoadingOperators } = useQuery({
    queryKey: ["grants-operators"],
    queryFn: async () => {
      const { data } = await apiClient.get("/grants/operators");
      return data;
    },
    enabled: isOpen,
  });

  const { data: competitionsData, isLoading: isLoadingCompetitions } = useQuery({
    queryKey: ["grants-competitions"],
    queryFn: async () => {
      const { data } = await apiClient.get("/grants/competitions");
      return data;
    },
    enabled: isOpen,
  });

  const { data: projectsData, isLoading: isLoadingProjects } = useQuery({
    queryKey: ["grants-projects"],
    queryFn: async () => {
      const { data } = await apiClient.get("/grants/projects");
      return data;
    },
    enabled: isOpen,
  });

  const { data: existingCall, isLoading: isLoadingCall } = useQuery({
    queryKey: ["grants-call", callId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/grants/calls/${callId}`);
      return data;
    },
    enabled: isOpen && isEditMode,
    staleTime: 0, // Always refetch when modal opens
  });

  // Populate form data when modal opens in edit mode
  // IMPORTANT: isOpen must be in dependencies to ensure this runs on every modal open
  // (not just when existingCall changes) - fixes race condition with resetForm
  useEffect(() => {
    if (isOpen && existingCall && isEditMode) {
      setFormData({
        title: existingCall.title || "",
        programId: existingCall.program?.id || "",
        operatorId: existingCall.operator?.id || "",
        competitionId: existingCall.competition?.id || "",
        projectId: existingCall.project?.id || "",
        callType: existingCall.callType || "COMPETITIVE",
        status: existingCall.status || "ANNOUNCED",
        grantType: existingCall.grantType || "",
        applicationStartDate: existingCall.applicationStartDate?.split("T")[0] || "",
        applicationEndDate: existingCall.applicationEndDate?.split("T")[0] || "",
        announcedAt: existingCall.announcedAt?.split("T")[0] || "",
        allocation: existingCall.allocation?.toString() || "",
        fundingMin: existingCall.fundingRange?.min?.toString() || "",
        fundingMax: existingCall.fundingRange?.max?.toString() || "",
        fundingPercentage: existingCall.fundingRange?.percentage?.toString() || "",
        fundingCurrency: existingCall.fundingRange?.currency || "PLN",
        targetRegions: existingCall.targetRegions?.join(", ") || "",
        targetGroup: existingCall.targetGroups?.join(", ") || existingCall.targetGroup || "",
        officialUrl: existingCall.officialUrl || "",
        applicationUrl: existingCall.applicationUrl || "",
      });
    }
  }, [isOpen, existingCall, isEditMode]);

  const createMutation = useMutation({
    mutationFn: async (payload: any) => {
      if (isEditMode && callId) {
        await apiClient.put(`/grants/calls/${callId}`, payload);
      } else {
        await apiClient.post("/grants/calls", payload);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants-calls"] });
      if (callId) {
        queryClient.invalidateQueries({ queryKey: ["grants-call", callId] });
      }
      onClose();
      resetForm();
    },
    onError: (error: any) => {
      if (error.response?.status >= 500) {
        setGlobalError('Blad serwera. Sprobuj ponownie pozniej.');
      } else if (!error.response) {
        setGlobalError('Blad polaczenia. Sprawdz polaczenie z internetem.');
      } else if (error.response?.data?.details) {
        const newErrors: Record<string, string> = {};
        error.response.data.details.forEach(
          (detail: { field: string; message: string }) => {
            newErrors[detail.field] = detail.message;
          }
        );
        setErrors(newErrors);
      } else {
        setGlobalError('Wystapil nieoczekiwany blad. Sprobuj ponownie.');
      }
    },
  });

  const resetForm = () => {
    setFormData({
      title: "",
      programId: "",
      operatorId: "",
      competitionId: "",
      projectId: "",
      callType: "COMPETITIVE",
      status: "ANNOUNCED",
      grantType: "",
      applicationStartDate: "",
      applicationEndDate: "",
      announcedAt: "",
      allocation: "",
      fundingMin: "",
      fundingMax: "",
      fundingPercentage: "",
      fundingCurrency: "PLN",
      targetRegions: "",
      targetGroup: "",
      officialUrl: "",
      applicationUrl: "",
    });
    setErrors({});
    setGlobalError(null);
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !createMutation.isPending) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, createMutation.isPending]);

  // Auto-focus first input when modal opens
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const parseNumber = (value: string) => {
    if (!value.trim()) return undefined;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  };

  const handleCompetitionChange = (competitionId: string) => {
    const competition = competitionsData?.competitions?.find(
      (item: any) => item.id === competitionId
    );

    setFormData((prev) => ({
      ...prev,
      competitionId,
      programId: competition?.program?.id || prev.programId,
    }));
  };

  const handleProjectChange = (projectId: string) => {
    const project = projectsData?.projects?.find(
      (item: any) => item.id === projectId
    );

    setFormData((prev) => ({
      ...prev,
      projectId,
      operatorId: project?.operator?.id || prev.operatorId,
      competitionId: project?.competition?.id || prev.competitionId,
      programId: project?.competition?.program?.id || prev.programId,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setGlobalError(null);

    const targetRegions = formData.targetRegions
      .split(",")
      .map((region) => region.trim())
      .filter(Boolean);

    const fundingMin = parseNumber(formData.fundingMin);
    const fundingMax = parseNumber(formData.fundingMax);
    const fundingPercentage = parseNumber(formData.fundingPercentage);
    const allocation = parseNumber(formData.allocation);

    const hasFundingRange =
      fundingMin !== undefined ||
      fundingMax !== undefined ||
      fundingPercentage !== undefined;

    const payload = {
      title: formData.title,
      programId: formData.programId,
      operatorId: formData.operatorId,
      competitionId: formData.competitionId || undefined,
      projectId: formData.projectId || undefined,
      callType: formData.callType,
      status: formData.status,
      grantType: formData.grantType || undefined,
      applicationStartDate: formData.applicationStartDate || undefined,
      applicationEndDate: formData.applicationEndDate || undefined,
      announcedAt: formData.announcedAt || undefined,
      allocation: allocation ?? undefined,
      fundingRange: hasFundingRange
        ? {
            min: fundingMin,
            max: fundingMax,
            percentage: fundingPercentage,
            currency: formData.fundingCurrency || "PLN",
          }
        : undefined,
      targetRegions,
      targetGroup: formData.targetGroup || undefined,
      officialUrl: formData.officialUrl || undefined,
      applicationUrl: formData.applicationUrl || undefined,
    };

    createMutation.mutate(payload);
  };

  const handleClose = () => {
    if (!createMutation.isPending) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const isLoading = createMutation.isPending;
  // Wait for ALL required data before showing form:
  // - In edit mode: existingCall + programs + operators (for dropdowns)
  // - In create mode: programs + operators only
  const isLoadingEditData = isEditMode && isLoadingCall;
  const isLoadingDropdowns = isLoadingPrograms || isLoadingOperators;
  const isLoadingFormData = isLoadingEditData || isLoadingDropdowns;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 my-8">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h2 className="text-lg font-medium text-neutral-900">
            {isEditMode ? "Edytuj nabór" : "Dodaj nabór"}
          </h2>
        </div>

        {isLoadingFormData ? (
          <div className="px-6 py-12 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <span className="ml-3 text-neutral-600">Ładowanie danych...</span>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {globalError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md" role="alert">
              <p className="text-sm text-red-700">{globalError}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Tytuł <span className="text-red-600">*</span>
            </label>
            <input
              ref={firstInputRef}
              type="text"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 ${
                errors.title ? "border-red-500" : "border-neutral-300"
              }`}
              required
            />
            {errors.title && (
              <p className="mt-1 text-xs text-red-600">{errors.title}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Program <span className="text-red-600">*</span>
              </label>
              <select
                value={formData.programId}
                onChange={(e) =>
                  setFormData({ ...formData, programId: e.target.value })
                }
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 ${
                  errors.programId ? "border-red-500" : "border-neutral-300"
                }`}
                required
                disabled={isLoadingPrograms}
              >
                {isLoadingPrograms ? (
                  <option value="">Ladowanie...</option>
                ) : (
                  <>
                    <option value="">Wybierz program</option>
                    {programsData?.programs?.map((program: any) => (
                      <option key={program.id} value={program.id}>
                        {program.code ? `${program.code} - ${program.name}` : program.name}
                      </option>
                    ))}
                  </>
                )}
              </select>
              {errors.programId && (
                <p className="mt-1 text-xs text-red-600">{errors.programId}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Operator <span className="text-red-600">*</span>
              </label>
              <select
                value={formData.operatorId}
                onChange={(e) =>
                  setFormData({ ...formData, operatorId: e.target.value })
                }
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 ${
                  errors.operatorId ? "border-red-500" : "border-neutral-300"
                }`}
                required
                disabled={isLoadingOperators}
              >
                {isLoadingOperators ? (
                  <option value="">Ladowanie...</option>
                ) : (
                  <>
                    <option value="">Wybierz operatora</option>
                    {operatorsData?.operators?.map((operator: any) => (
                      <option key={operator.id} value={operator.id}>
                        {operator.shortName || operator.name}
                      </option>
                    ))}
                  </>
                )}
              </select>
              {errors.operatorId && (
                <p className="mt-1 text-xs text-red-600">{errors.operatorId}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Konkurs
              </label>
              <select
                value={formData.competitionId}
                onChange={(e) => handleCompetitionChange(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                disabled={isLoadingCompetitions}
              >
                {isLoadingCompetitions ? (
                  <option value="">Ladowanie...</option>
                ) : (
                  <>
                    <option value="">Brak</option>
                    {competitionsData?.competitions?.map((competition: any) => (
                      <option key={competition.id} value={competition.id}>
                        {competition.program?.code
                          ? `${competition.program.code} - ${competition.name}`
                          : competition.name}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Projekt operatora
              </label>
              <select
                value={formData.projectId}
                onChange={(e) => handleProjectChange(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                disabled={isLoadingProjects}
              >
                {isLoadingProjects ? (
                  <option value="">Ladowanie...</option>
                ) : (
                  <>
                    <option value="">Brak</option>
                    {projectsData?.projects?.map((project: any) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                        {project.operator?.shortName || project.operator?.name
                          ? ` - ${
                              project.operator?.shortName || project.operator?.name
                            }`
                          : ""}
                        {project.competition?.name
                          ? ` - ${project.competition.name}`
                          : ""}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Typ naboru
              </label>
              <select
                value={formData.callType}
                onChange={(e) =>
                  setFormData({ ...formData, callType: e.target.value })
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
              >
                {CALL_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
              >
                {CALL_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Data rozpoczęcia
              </label>
              <input
                type="date"
                value={formData.applicationStartDate}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    applicationStartDate: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Data zakończenia
              </label>
              <input
                type="date"
                value={formData.applicationEndDate}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    applicationEndDate: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Data ogłoszenia
              </label>
              <input
                type="date"
                value={formData.announcedAt}
                onChange={(e) =>
                  setFormData({ ...formData, announcedAt: e.target.value })
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Alokacja (PLN)
              </label>
              <input
                type="number"
                value={formData.allocation}
                onChange={(e) =>
                  setFormData({ ...formData, allocation: e.target.value })
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Grupa docelowa
              </label>
              <input
                type="text"
                value={formData.targetGroup}
                onChange={(e) =>
                  setFormData({ ...formData, targetGroup: e.target.value })
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Regiony (oddzielone przecinkami)
            </label>
            <input
              type="text"
              value={formData.targetRegions}
              onChange={(e) =>
                setFormData({ ...formData, targetRegions: e.target.value })
              }
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
              placeholder="mazowieckie, lubelskie"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Min. dofinansowanie
              </label>
              <input
                type="number"
                value={formData.fundingMin}
                onChange={(e) =>
                  setFormData({ ...formData, fundingMin: e.target.value })
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Max. dofinansowanie
              </label>
              <input
                type="number"
                value={formData.fundingMax}
                onChange={(e) =>
                  setFormData({ ...formData, fundingMax: e.target.value })
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Proc. dofinansowania
              </label>
              <input
                type="number"
                value={formData.fundingPercentage}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    fundingPercentage: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                min="0"
                max="100"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Rodzaj wsparcia
            </label>
            <input
              type="text"
              value={formData.grantType}
              onChange={(e) =>
                setFormData({ ...formData, grantType: e.target.value })
              }
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
              placeholder="Szkolenia, doradztwo"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Strona oficjalna
              </label>
              <input
                type="url"
                value={formData.officialUrl}
                onChange={(e) =>
                  setFormData({ ...formData, officialUrl: e.target.value })
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Link do wniosku
              </label>
              <input
                type="url"
                value={formData.applicationUrl}
                onChange={(e) =>
                  setFormData({ ...formData, applicationUrl: e.target.value })
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        </form>
        )}

        <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading || isLoadingFormData}
            className="px-4 py-2 text-sm font-medium text-neutral-700 bg-neutral-100 rounded-md hover:bg-neutral-200 disabled:opacity-50"
          >
            Anuluj
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || isLoadingFormData}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading && (
              <div role="status" aria-live="polite">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span className="sr-only">Zapisywanie...</span>
              </div>
            )}
            {isLoading ? "Zapisywanie..." : isEditMode ? "Zapisz zmiany" : "Dodaj nabór"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateCallModal;
